/**
 * Auto Route — the options, the progress, and what it did.
 *
 * The log is the original's own, columns and all (`OGroute9.cpp`): one row per
 * pass, the hit ratio beside the count, the elapsed time so a slow board says
 * which pass was slow. It is a good design and there was no reason to invent
 * another.
 *
 * The run is cancellable because on a large board it takes a minute and a half.
 */

import type { AutoRouteResult } from '@sircad/core/route/pathfinder.ts';
import { button, buttons, openDialog } from './dialog.ts';

export interface AutoRouteChosen {
  ripUp: 'auto' | 'all' | 'none';
  maxIterations: number;
  /** Copper layers the router may lay track on. */
  layers: number[];
  /**
   * Supply nets to pour before routing, by the layer each goes on.
   *
   * Empty pours nothing, and every supply is then routed as copper like any
   * other net. That is the honest default on a board where nobody has given
   * ground a layer: a supply routed as tracks is a worse ground than a plane
   * and a great deal better than no ground.
   */
  pour: Record<string, number>;
}

/** A supply the board could pour, and where it would go. */
export interface PourOffer {
  net: string;
  layer: number;
  layerName: string;
  pins: number;
  /** `assigned` when a layer already names it — those are ticked to begin with. */
  assigned: boolean;
}

/** A copper layer the router could use, and whether it starts ticked. */
export interface RoutableLayer {
  num: number;
  name: string;
  on: boolean;
}

export interface AutoRouteDialogOptions {
  /** Connections still to make, so the dialog can say what it is about to do. */
  open: number;
  /** Tracks already laid by the router, which a re-run would replace. */
  previous: number;
  /**
   * The board's copper, and which of it the router will use unless told
   * otherwise.
   *
   * Offered because the number of sides is a decision about the board and not
   * something the file states: a single-sided board and an unrouted two-sided
   * one look identical from the layer table. The default is the whole stack
   * the router would pick for itself, so the common case is to leave it alone.
   */
  layers: RoutableLayer[];
  /**
   * Supplies that could be poured rather than routed.
   *
   * Offered, not taken: a net silently promoted to a plane is a board that
   * fails in a way nobody looks for. Anything a layer already names is ticked,
   * because that is not a guess.
   */
  pourOffers: PourOffer[];
  formatLength(inches: number): string;
  /**
   * Start a run. Report progress through `onProgress`; resolve when finished.
   * Calling the returned function cancels it.
   */
  start(
    chosen: AutoRouteChosen,
    onProgress: (stage: string, fraction: number, routed: number, total: number) => void,
  ): { done: Promise<AutoRouteResult>; cancel: () => void };
  /** Land the copper, after checking it. Returns what went wrong, or null. */
  apply(result: AutoRouteResult): string | null;
  /** Write the statistics log out. */
  onSave(result: AutoRouteResult): void;
}

export function showAutoRouteDialog(opts: AutoRouteDialogOptions): void {
  openDialog({
    title: 'Auto Route',
    wide: true,
    build(body, handle) {
      const form = document.createElement('div');
      form.className = 'place-form';

      const row = (label: string, control: HTMLElement, why?: string): void => {
        const line = document.createElement('label');
        line.className = 'place-row';
        const text = document.createElement('span');
        text.textContent = label;
        if (why) text.title = why;
        line.append(control, text);
        form.appendChild(line);
      };

      const rip = document.createElement('select');
      for (const [label, value] of [
        ['Replace my previous auto-routes', 'auto'],
        ['Tear up every track first', 'all'],
        ['Keep everything, route the rest', 'none'],
      ] as const) {
        const o = document.createElement('option');
        o.value = value;
        o.textContent = label;
        rip.appendChild(o);
      }
      rip.value = 'auto';
      row('Start from', rip,
        'Hand-drawn copper is never removed by the first option — only what the '
        + 'router laid itself.');

      const effort = document.createElement('select');
      for (const [label, value] of [['Quick', '6'], ['Normal', '15'], ['Thorough', '30']] as const) {
        const o = document.createElement('option');
        o.value = value;
        o.textContent = label;
        effort.appendChild(o);
      }
      effort.value = '15';
      row('Effort', effort, 'Rounds of negotiation before it settles for what it has.');

      // One box per copper layer rather than a "sides" number: a four-layer
      // board is not two boards, and the inner layers have names worth reading.
      //
      // Not built with `row`, which wraps its control in a label: each tick
      // needs a label of its own, and a label inside a label sends every click
      // in the row to whichever box happens to be first.
      const layerRow = document.createElement('div');
      layerRow.className = 'place-row';
      const layerLabel = document.createElement('span');
      layerLabel.textContent = 'Layers';
      layerLabel.title = 'Which copper the router may use. One layer means no vias, '
        + 'so a connection that has to cross another is left for you.';
      const layerBox = document.createElement('div');
      layerBox.className = 'route-layers';
      const boxes: { num: number; input: HTMLInputElement }[] = [];
      for (const l of opts.layers) {
        const tick = document.createElement('label');
        const input = document.createElement('input');
        input.type = 'checkbox';
        input.checked = l.on;
        tick.append(input, document.createTextNode(l.name));
        layerBox.appendChild(tick);
        boxes.push({ num: l.num, input });
      }
      layerRow.append(layerLabel, layerBox);
      form.appendChild(layerRow);
      const chosenLayers = (): number[] =>
        boxes.filter((b) => b.input.checked).map((b) => b.num);

      // The supplies, and whether to pour them.
      //
      // Ticked, a net is poured onto its layer before anything is routed and
      // the router then leaves it alone. Unticked, it is routed as copper like
      // any other net — which is the honest answer on a board where nobody has
      // given ground a layer, and better than the old behaviour, where the
      // router skipped every supply on the understanding that a pour would
      // carry it and no pour ever did.
      const pourBoxes: { net: string; layer: number; input: HTMLInputElement }[] = [];
      if (opts.pourOffers.length > 0) {
        const pourRow = document.createElement('div');
        pourRow.className = 'place-row';
        const pourLabel = document.createElement('span');
        pourLabel.textContent = 'Pour';
        pourLabel.title = 'A supply poured onto a layer is not routed as tracks. '
          + 'Left unticked it is routed like any other net.';
        const pourBox = document.createElement('div');
        pourBox.className = 'route-layers';
        for (const o of opts.pourOffers) {
          const tick = document.createElement('label');
          const input = document.createElement('input');
          input.type = 'checkbox';
          input.checked = o.assigned;
          input.title = o.assigned
            ? `${o.layerName} already names ${o.net}`
            : `${o.net} has ${o.pins} pins and no layer of its own yet`;
          tick.append(input, document.createTextNode(`${o.net} → ${o.layerName}`));
          pourBox.appendChild(tick);
          pourBoxes.push({ net: o.net, layer: o.layer, input });
        }
        pourRow.append(pourLabel, pourBox);
        form.appendChild(pourRow);
      }
      const chosenPour = (): Record<string, number> => {
        const out: Record<string, number> = {};
        for (const b of pourBoxes) if (b.input.checked) out[b.net] = b.layer;
        return out;
      };

      body.appendChild(form);

      const note = document.createElement('p');
      note.className = 'dfm-note';
      note.textContent = `${opts.open} connection${opts.open === 1 ? '' : 's'} to make`
        + (opts.previous > 0 ? `, ${opts.previous} previously auto-routed` : '')
        + '.';
      body.appendChild(note);

      const out = document.createElement('div');
      out.className = 'place-result';
      body.appendChild(out);

      const bar = buttons(body);
      let running: { cancel: () => void } | null = null;
      let found: AutoRouteResult | null = null;

      const applyBtn = button(bar, 'Apply', () => {
        if (!found) return;
        const trouble = opts.apply(found);
        if (trouble) { out.textContent = trouble; return; }
        handle.close();
      });
      applyBtn.classList.add('primary');
      applyBtn.disabled = true;

      const saveBtn = button(bar, 'Save log…', () => { if (found) opts.onSave(found); });
      saveBtn.disabled = true;

      const runBtn = button(bar, 'Route', () => {
        if (running) { running.cancel(); running = null; runBtn.textContent = 'Route'; return; }

        const layers = chosenLayers();
        if (layers.length === 0) {
          out.textContent = 'Tick at least one copper layer for the router to use.';
          return;
        }

        applyBtn.disabled = true;
        saveBtn.disabled = true;
        runBtn.textContent = 'Cancel';
        out.textContent = 'Starting…';

        const job = opts.start(
          {
            ripUp: rip.value as AutoRouteChosen['ripUp'],
            maxIterations: Number(effort.value) || 15,
            layers,
            pour: chosenPour(),
          },
          (stage, fraction, routed, total) => {
            out.textContent = `${stage} — ${routed} of ${total}`
              + ` (${Math.round(fraction * 100)}%)`;
          },
        );
        running = job;

        void job.done.then((result) => {
          running = null;
          runBtn.textContent = 'Route';
          found = result;
          applyBtn.disabled = result.add.length === 0;
          saveBtn.disabled = false;
          out.textContent = '';

          const stats = document.createElement('dl');
          stats.className = 'density-stats';
          const stat = (label: string, value: string): void => {
            const dt = document.createElement('dt');
            dt.textContent = label;
            const dd = document.createElement('dd');
            dd.textContent = value;
            stats.append(dt, dd);
          };
          stat('Connections', `${result.routed} of ${result.total}`);
          stat('Copper laid', opts.formatLength(result.length));
          stat('Vias', String(result.vias));
          stat('Passes', result.converged
            ? `${result.iterations} — settled` : `${result.iterations} — did not settle`);
          out.appendChild(stats);

          if (result.unrouted.length > 0) {
            const p = document.createElement('p');
            p.className = 'dfm-note';
            p.textContent = `Left for you (${result.unrouted.length}): `
              + result.unrouted.slice(0, 15).join(', ')
              + (result.unrouted.length > 15 ? '…' : '');
            out.appendChild(p);
          }

          const log = document.createElement('pre');
          log.className = 'route-log';
          log.textContent = result.stats
            .map((s) => `${s.label.padEnd(30).slice(0, 30)}`
              + `${String(s.processed).padStart(8)}${String(s.routed).padStart(8)}`
              + `${(s.ms / 1000).toFixed(2).padStart(9)}s`)
            .join('\n');
          out.appendChild(log);
        }).catch((err: unknown) => {
          running = null;
          runBtn.textContent = 'Route';
          out.textContent = `Auto Route failed: ${String(err)}`;
        });
      });

      button(bar, 'Close', () => {
        if (running) running.cancel();
        handle.close();
      });
    },
  });
}
